// src/pages/MentorProfilePage.greentech.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BigFiveModal from '../components/BigFiveModal';
import { Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// GREENTECH_MATCHING_PREFS_UI_v1
// Greentech Program Matching Prefs (Survey 28)
// Fixe Auswahllisten, keine Auto-Vorschläge / kein Prefill.
const GREENTECH_SURVEY_ID = 28;
const GREENTECH_MAX_SELECTION = 6;

const GREENTECH_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
];

const GREENTECH_APPLICATION_FIELDS = [
  'Renewable Energy Systems',
  'Smart Grids & Flexibility',
  'Energy Markets & Regulation',
  'Green Hydrogen & Power-to-X',
  'Battery & Storage Technologies',
  'Sustainable Mobility & Public Transit',
  'Climate Risk, Flooding & Resilience',
  'Nature-based Solutions & Ecosystems',
  'Sustainable Water & Sanitation',
  'Circular Economy & Recycling',
  'Waste-to-Value & Biomass',
  'Precision Agriculture & Food Systems',
  'Earth Observation & Environmental Monitoring',
  'Green Finance & ESG / Anti-Greenwashing',
  'Sustainable Cities & Buildings',
  'Industrial Decarbonization',
];

const GREENTECH_METHODS_SKILLS = [
  'Remote Sensing',
  'GIS / Geospatial Analysis',
  'Machine Learning',
  'Data Science & Statistics',
  'Time-Series Forecasting',
  'Optimization / Operations Research',
  'System Modelling (Energy/Transport)',
  'Life Cycle Assessment (LCA)',
  'Techno-Economic Analysis (TEA)',
  'Computer Vision',
  'IoT / Sensor Systems',
  'Simulation (Agent-based, Monte Carlo)',
  'Policy & Impact Evaluation',
  'Laboratory / Microbiology Methods',
  'Bioinformatics / Genomics',
  'Robotics / Automation',
];

// Legacy → aktuelle Label (wichtig: exakt wie deine Checkbox-Labels)
const GREENTECH_LEGACY_MAP_APPLICATION_FIELDS = {
  'Nature-based Solutions': 'Nature-based Solutions & Ecosystems',
};

// Optional: falls alte Sprachwerte im Umlauf sind
const GREENTECH_LEGACY_MAP_LANGUAGES = {
  English: 'en',
  Deutsch: 'de',
};

const normalizeEnumArray = (arr, allowed, max, legacyMap = {}) => {
  const out = [];
  const src = Array.isArray(arr) ? arr : [];

  for (const raw of src) {
    const s0 = String(raw ?? '').trim();
    if (!s0) continue;

    const s = legacyMap[s0] ?? s0;
    if (!allowed.includes(s)) continue;

    if (!out.includes(s)) out.push(s);
    if (out.length >= max) break;
  }

  return out;
};

// ✅ NEU: sicherstellen, dass goals/competences IMMER Arrays sind (auch wenn Backend Strings liefert)
const coerceArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

function MentorProfilePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // ✅ NEU (Crash-Sicherung): i18n Arrays immer als Array zurückgeben, sonst []
  const tArray = (key) => {
    const v = t(key, { returnObjects: true });
    return Array.isArray(v) ? v : [];
  };

  const [firstTimeSaved, setFirstTimeSaved] = useState(false);
  const [showBigFiveModal, setShowBigFiveModal] = useState(false);
  const [userId, setUserId] = useState(null);

  const [bigfiveData, setBigfiveData] = useState(null);
  const [hasBigFive, setHasBigFive] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [avatarPath, setAvatarPath] = useState('');
  const [remoteImageBase64, setRemoteImageBase64] = useState(null);

  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [mailValue, setMailValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [standortValue, setStandortValue] = useState('');
  const [abteilungValue, setAbteilungValue] = useState('');
  const [jobTitleValue, setJobTitleValue] = useState('');

  // ✅ Freitext: wird ab jetzt als profile_description gespeichert/geladen
  const [profileDescriptionValue, setProfileDescriptionValue] = useState('');

  const [experienceValue, setExperienceValue] = useState('0-2 Jahre');
  const [goals, setGoals] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [whyMentoring, setWhyMentoring] = useState('');

  // --- GreenTech Matching Prefs (Survey 28) ---
  const [gtPrefsAvailable, setGtPrefsAvailable] = useState(true);
  const [gtPrefsLoading, setGtPrefsLoading] = useState(true);
  const [gtPrefsSaving, setGtPrefsSaving] = useState(false);

  // ✅ Anpassung: success-String -> message-String + "savedOk"-Flag
  const [gtPrefsMessage, setGtPrefsMessage] = useState('');
  const [gtPrefsError, setGtPrefsError] = useState('');
  const [gtPrefsSavedOk, setGtPrefsSavedOk] = useState(false);

  const [gtLanguages, setGtLanguages] = useState([]);
  const [gtMentorBackground, setGtMentorBackground] = useState(null); // 'science' | 'industry'
  const [gtMentorMaxMentees, setGtMentorMaxMentees] = useState(null); // number
  const [gtApplicationFields, setGtApplicationFields] = useState([]);
  const [gtMethodsSkills, setGtMethodsSkills] = useState([]);

  const toggleInList = (value, list, setList, max = null) => {
    if (!value) return;

    // ✅ Sobald geändert wird: "gespeichert"-Status zurücksetzen + Hinweise leeren
    setGtPrefsSavedOk(false);
    setGtPrefsMessage('');
    setGtPrefsError('');

    const safeList = Array.isArray(list) ? list : [];

    if (safeList.includes(value)) {
      setList(safeList.filter(v => v !== value));
      return;
    }
    if (typeof max === 'number' && max > 0 && safeList.length >= max) {
      return; // hard cap
    }
    setList([...safeList, value]);
  };

  // Übersetzbar machen (mit Absicherung: returnObjects kann String liefern)
  const devGoalsRaw = t('mentorProfile.devGoals', { returnObjects: true });
  const competenceDataRaw = t('mentorProfile.competenceData', { returnObjects: true });

  const devGoals = Array.isArray(devGoalsRaw) ? devGoalsRaw : [];
  const competenceData = Array.isArray(competenceDataRaw) ? competenceDataRaw : [];

  // --- i18n für GreenTech-Block: nutzt Übersetzungen, fallbackt sauber auf DE/EN ---
  const isDE = String(i18n?.language || '').toLowerCase().startsWith('de');
  const gtT = (key, de, en) => t(key, { defaultValue: isDE ? de : en });
  const tr = (de, en) => (isDE ? de : en);

  // ✅ Prefs-Validierung für Prefs-Speicherbutton + Hinweistext
  const mmForValidity = (gtMentorMaxMentees == null) ? null : Number(gtMentorMaxMentees);

  const gtPrefsIsValid =
    Array.isArray(gtLanguages) && gtLanguages.length > 0 &&
    Boolean(gtMentorBackground) &&
    (typeof mmForValidity === 'number' && Number.isFinite(mmForValidity) && mmForValidity > 0) &&
    Array.isArray(gtApplicationFields) && gtApplicationFields.length > 0 &&
    Array.isArray(gtMethodsSkills) && gtMethodsSkills.length > 0;

  const gtPrefsMissingHint = (() => {
    if (!Array.isArray(gtLanguages) || gtLanguages.length === 0) {
      return gtT(
        'mentorProfile.gtPrefs.errNeedLanguage',
        'Bitte mindestens eine Sprache auswählen.',
        'Please select at least one language.'
      );
    }
    if (!gtMentorBackground) {
      return gtT(
        'mentorProfile.gtPrefs.errNeedBackground',
        'Bitte auswählen, ob Du aus Wissenschaft/Forschung oder Industrie kommst.',
        'Please select whether you are from academia/research or industry.'
      );
    }
    if (!(typeof mmForValidity === 'number' && Number.isFinite(mmForValidity) && mmForValidity > 0)) {
      return gtT(
        'mentorProfile.gtPrefs.errNeedMaxMentees',
        'Bitte die maximale Anzahl an Mentees wählen.',
        'Please choose your maximum number of mentees.'
      );
    }
    if (!Array.isArray(gtApplicationFields) || gtApplicationFields.length === 0) {
      return gtT(
        'mentorProfile.gtPrefs.errNeedApplicationField',
        'Bitte mindestens ein Anwendungsfeld auswählen.',
        'Please select at least one application field.'
      );
    }
    if (!Array.isArray(gtMethodsSkills) || gtMethodsSkills.length === 0) {
      return gtT(
        'mentorProfile.gtPrefs.errNeedMethodsSkills',
        'Bitte mindestens eine Methode / Skill auswählen.',
        'Please select at least one method / skill.'
      );
    }
    return '';
  })();

  // ✅ Profil-Pflichtfelder + Gesamt-Gate für "Profil speichern" Button
  const firstNameOk = String(firstNameValue || '').trim().length > 0;
  const lastNameOk = String(lastNameValue || '').trim().length > 0;

  // Abteilung + Position + Beschreibung + Berufserfahrung (Auswahl)
  const departmentOk = String(abteilungValue || '').trim().length > 0;
  const jobTitleOk = String(jobTitleValue || '').trim().length > 0;
  const profileDescriptionOk = String(profileDescriptionValue || '').trim().length > 0;
  const experienceOk = String(experienceValue || '').trim().length > 0;

  // Entwicklungsziele + Kompetenzen
  const goalsOk = Array.isArray(goals) && goals.length > 0;
  const competencesOk = Array.isArray(competences) && competences.length > 0;

  const profileIsValid =
    firstNameOk &&
    lastNameOk &&
    departmentOk &&
    jobTitleOk &&
    profileDescriptionOk &&
    experienceOk &&
    goalsOk &&
    competencesOk;

  // ✅ Fehlende Pflichtfelder-Labels für Hinweis unter dem Speicherbutton
  const missingProfileFields = [];
  if (!firstNameOk) missingProfileFields.push(tr('Vorname', 'First name'));
  if (!lastNameOk) missingProfileFields.push(tr('Nachname', 'Last name'));
  if (!departmentOk) missingProfileFields.push(tr('Abteilung', 'Department'));
  if (!jobTitleOk) missingProfileFields.push(tr('Position', 'Position'));
  if (!profileDescriptionOk) missingProfileFields.push(tr('Beschreibung deiner Erfahrung', 'Experience summary'));
  if (!experienceOk) missingProfileFields.push(tr('Dauer der Berufserfahrung', 'Experience duration'));
  if (!goalsOk) missingProfileFields.push(tr('Entwicklungsziele', 'Development goals'));
  if (!competencesOk) missingProfileFields.push(tr('Kompetenzen', 'Competences'));

  // Profil speichern nur, wenn (a) Profilfelder ok und (b) Prefs gespeichert (wenn aktiv)
  const profileSaveIsAllowed =
    profileIsValid &&
    (!gtPrefsAvailable || gtPrefsSavedOk);

  // Beim Profil-Speichern-Button: Hinweise welche Pflichtfelder fehlen ODER Prefs fehlen
  const showSaveMissingFieldsHint = !profileIsValid;
  const showPrefsMustBeSavedHint =
    gtPrefsAvailable &&
    profileIsValid &&
    !gtPrefsSavedOk;

  useEffect(() => {
    axios.get('/mentoring/mentor/profile', { withCredentials: true })
      .then(res => {
        const data = res.data || {};

        if (data.avatarPath) setAvatarPath(String(data.avatarPath).replace(/\\/g, '/'));
        if (data.avatar) setRemoteImageBase64(`data:image/png;base64,${data.avatar}`);

        if (data.email) setMailValue(data.email);
        if (data.firstName) setFirstNameValue(data.firstName);
        if (data.lastName) setLastNameValue(data.lastName);
        if (data.phone) setPhoneValue(data.phone);
        if (data.standort) setStandortValue(data.standort);
        if (data.abteilung) setAbteilungValue(data.abteilung);
        if (data.jobTitle) setJobTitleValue(data.jobTitle);

        // ✅ NEU: Freitext aus profileDescription (mit Fallbacks für alte Daten)
        if (typeof data.profileDescription === 'string') {
          setProfileDescriptionValue(data.profileDescription);
        } else if (typeof data.branchenKnow === 'string') {
          setProfileDescriptionValue(data.branchenKnow);
        } else if (typeof data.branchName === 'string') {
          setProfileDescriptionValue(data.branchName);
        }

        if (data.experience) setExperienceValue(data.experience);

        // ✅ FIX: goals/competences IMMER als Array halten (sonst klicken kaputt)
        setGoals(coerceArray(data.goals));
        setCompetences(coerceArray(data.competences));

        if (data.whyMentoring) setWhyMentoring(data.whyMentoring);
        if (data.user_id) setUserId(data.user_id);

        // BigFive Daten aus der API übernehmen (falls vorhanden) – robust (nicht nur truthy)
        if (data.bigfive_extraversion != null) {
          setBigfiveData({
            extraversion: data.bigfive_extraversion,
            gewissenhaftigkeit: data.bigfive_gewissenhaftigkeit,
            negative_emotionalitaet: data.bigfive_negative_emotionalitaet,
            vertraeglichkeit: data.bigfive_vertraeglichkeit,
            offenheit: data.bigfive_offenheit,
            extraversion_text: data.bigfive_extraversion_text,
            gewissenhaftigkeit_text: data.bigfive_gewissenhaftigkeit_text,
            negative_emotionalitaet_text: data.bigfive_negative_emotionalitaet_text,
            vertraeglichkeit_text: data.bigfive_vertraeglichkeit_text,
            offenheit_text: data.bigfive_offenheit_text
          });
        }

        const alreadyHas = Boolean(data.bigfive_updated_at) || (data.bigfive_extraversion != null);
        setHasBigFive(alreadyHas);
      })
      .catch(console.error);
  }, []);

  // Load GreenTech Matching Prefs (Survey 28) if allowed for this tenant
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setGtPrefsError('');
      setGtPrefsMessage('');
      setGtPrefsLoading(true);
      setGtPrefsSavedOk(false);

      try {
        const res = await axios.get(`/mentoring/survey-matching-prefs/${GREENTECH_SURVEY_ID}`, { withCredentials: true });
        if (cancelled) return;

        const d = res.data || {};
        setGtPrefsAvailable(true);

        const allowedLangs = GREENTECH_LANGUAGE_OPTIONS.map(o => o.value);

        setGtLanguages(
          normalizeEnumArray(d.languages, allowedLangs, 10, GREENTECH_LEGACY_MAP_LANGUAGES)
        );
        setGtMentorBackground(d.mentor_background ?? null);

        const maxM = d.mentor_max_mentees;
        setGtMentorMaxMentees(
          (typeof maxM === 'number' && Number.isFinite(maxM)) ? maxM : (maxM == null ? null : Number(maxM))
        );

        setGtApplicationFields(
          normalizeEnumArray(
            d.application_fields,
            GREENTECH_APPLICATION_FIELDS,
            GREENTECH_MAX_SELECTION,
            GREENTECH_LEGACY_MAP_APPLICATION_FIELDS
          )
        );
        setGtMethodsSkills(
          normalizeEnumArray(d.methods_skills, GREENTECH_METHODS_SKILLS, GREENTECH_MAX_SELECTION)
        );

        // Nur als "gespeichert" markieren wenn die geladenen Daten tatsächlich vollständig sind.
        // Leere Backend-Rows (Erstzugriff) dürfen den Gate nicht öffnen.
        const mm = d.mentor_max_mentees;
        const loadedPrefsAreValid =
          Array.isArray(d.languages) && d.languages.length > 0 &&
          Boolean(d.mentor_background) &&
          (mm != null && Number.isFinite(Number(mm)) && Number(mm) > 0) &&
          Array.isArray(d.application_fields) && d.application_fields.length > 0 &&
          Array.isArray(d.methods_skills) && d.methods_skills.length > 0;
        setGtPrefsSavedOk(loadedPrefsAreValid);
      } catch (err) {
        if (cancelled) return;

        const status = err?.response?.status;
        const code = err?.response?.data?.error;

        if (status === 403 && (code === 'invalid_tenant_survey' || code === 'forbidden')) {
          setGtPrefsAvailable(false);
          setGtPrefsLoading(false);
          return;
        }

        setGtPrefsError(String(code || err?.message || gtT(
          'mentorProfile.gtPrefs.loadFailed',
          'Prefs konnten nicht geladen werden.',
          'Preferences could not be loaded.'
        )));
      } finally {
        if (!cancelled) setGtPrefsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const saveGreenTechPrefs = async ({ alertOnError = false } = {}) => {
    const fail = (msg) => {
      setGtPrefsError(msg);
      setGtPrefsMessage('');
      setGtPrefsSavedOk(false);
      if (alertOnError) alert(msg);
      return false;
    };

    setGtPrefsError('');
    setGtPrefsMessage('');

    if (!Array.isArray(gtLanguages) || gtLanguages.length === 0) {
      return fail(gtT(
        'mentorProfile.gtPrefs.errNeedLanguage',
        'Bitte mindestens eine Sprache auswählen (hartes Muss).',
        'Please select at least one language (hard requirement).'
      ));
    }
    if (!gtMentorBackground) {
      return fail(gtT(
        'mentorProfile.gtPrefs.errNeedBackground',
        'Bitte auswählen, ob Du aus Wissenschaft/Forschung oder Industrie kommst.',
        'Please select whether you are from academia/research or industry.'
      ));
    }
    const mm = (gtMentorMaxMentees == null) ? null : Number(gtMentorMaxMentees);
    if (!(typeof mm === 'number' && Number.isFinite(mm) && mm > 0)) {
      return fail(gtT(
        'mentorProfile.gtPrefs.errNeedMaxMentees',
        'Bitte wähle die maximale Anzahl an Mentees in diesem Programm.',
        'Please choose your maximum number of mentees for this program.'
      ));
    }

    // Pflichtfelder (Anwendungsfelder + Methoden/Skills)
    if (!Array.isArray(gtApplicationFields) || gtApplicationFields.length === 0) {
      return fail(gtT(
        'mentorProfile.gtPrefs.errNeedApplicationField',
        'Bitte mindestens ein Anwendungsfeld auswählen.',
        'Please select at least one application field.'
      ));
    }
    if (!Array.isArray(gtMethodsSkills) || gtMethodsSkills.length === 0) {
      return fail(gtT(
        'mentorProfile.gtPrefs.errNeedMethodsSkills',
        'Bitte mindestens eine Methode / Skill auswählen.',
        'Please select at least one method / skill.'
      ));
    }

    const allowedLangs = GREENTECH_LANGUAGE_OPTIONS.map(o => o.value);

    const payload = {
      languages: normalizeEnumArray(gtLanguages, allowedLangs, 10, GREENTECH_LEGACY_MAP_LANGUAGES),

      // Mentor-spezifisch
      mentor_background: gtMentorBackground,
      mentor_max_mentees: mm,

      // ✅ Hotfix: Backend verlangt noch mentor_preference
      mentor_preference: gtMentorBackground,

      application_fields: normalizeEnumArray(
        gtApplicationFields,
        GREENTECH_APPLICATION_FIELDS,
        GREENTECH_MAX_SELECTION,
        GREENTECH_LEGACY_MAP_APPLICATION_FIELDS
      ),
      methods_skills: normalizeEnumArray(gtMethodsSkills, GREENTECH_METHODS_SKILLS, GREENTECH_MAX_SELECTION),
    };

    try {
      setGtPrefsSaving(true);
      const res = await axios.put(`/mentoring/survey-matching-prefs/${GREENTECH_SURVEY_ID}`, payload, { withCredentials: true });

      if (res?.data?.success) {
        setGtPrefsMessage(gtT(
          'mentorProfile.gtPrefs.saved',
          'Gespeichert.',
          'Saved.'
        ));
        setGtPrefsSavedOk(true);
        return true;
      }
      return fail(String(res?.data?.error || 'save_failed'));
    } catch (err) {
      return fail(String(err?.response?.data?.error || err?.message || 'save_failed'));
    } finally {
      setGtPrefsSaving(false);
    }
  };

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    axios.post('/mentoring/upload/image', formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then(res => {
        setAvatarPath(res.data.filePath.replace(/^uploads[\\/]+/, ''));
      })
      .catch(console.error);
  }

  // MAX 4 Entwicklungsziele auswählbar
  function handleDevGoalChange(goal) {
    const safeGoals = Array.isArray(goals) ? goals : [];
    if (safeGoals.includes(goal)) {
      setGoals(safeGoals.filter(g => g !== goal));
    } else if (safeGoals.length < 4) {
      setGoals([...safeGoals, goal]);
    }
  }

  // MAX 16 Kompetenzen/Felder auswählbar (Mentor)
  function handleCompetenceChange(item) {
    const safeCompetences = Array.isArray(competences) ? competences : [];
    if (safeCompetences.includes(item)) {
      setCompetences(safeCompetences.filter(c => c !== item));
    } else if (safeCompetences.length < 16) {
      setCompetences([...safeCompetences, item]);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // Guards bleiben (falls jemand disabled via DevTools umgeht)
    if (!profileIsValid) {
      alert(gtT(
        'mentorProfile.errNeedCoreFields',
        'Bitte alle Pflichtfelder ausfüllen.',
        'Please fill all required fields.'
      ));
      return;
    }

    if (gtPrefsAvailable) {
      if (!gtPrefsSavedOk) {
        alert(gtT(
          'mentorProfile.gtPrefs.mustBeSaved',
          'Bitte Matching-Präferenzen zuerst speichern.',
          'Please save your matching preferences first.'
        ));
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('avatarPath', avatarPath);
      formData.append('firstName', firstNameValue);
      formData.append('lastName', lastNameValue);
      formData.append('email', mailValue); // Backend ignoriert das (Session ist Quelle)
      formData.append('phone', phoneValue);
      formData.append('standort', standortValue);
      formData.append('abteilung', abteilungValue);
      formData.append('jobTitle', jobTitleValue);

      // ✅ NEU: wird im Backend in profile_description gespeichert (Survey 28)
      formData.append('profileDescription', profileDescriptionValue);

      formData.append('experience', experienceValue);
      formData.append('goals', JSON.stringify(Array.isArray(goals) ? goals : []));
      formData.append('competences', JSON.stringify(Array.isArray(competences) ? competences : []));
      formData.append('whyMentoring', whyMentoring);

      await axios.post('/mentoring/mentor/profile', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert(t('mentorProfile.saveSuccess'));

      if (!firstTimeSaved) setFirstTimeSaved(true);
      if (!hasBigFive) setShowBigFiveModal(true);
    } catch (err) {
      console.error(err);
      alert(t('mentorProfile.saveError'));
    }
  }

  const displayedImage = avatarPath
    ? `/mentoring/uploads/${avatarPath}`
    : (remoteImageBase64 || null);

  // ✅ Für Markierung Pflichtfeld: nur Sternchen
  const ReqStar = () => <span className="text-red-400 ml-1">*</span>;

  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Zurück-Button */}
      <div className="flex justify-start mb-6">
        <button
          type="button"
          onClick={() => navigate('/mentor-dashboard')}
          className="flex items-center gap-2 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl hover:bg-green-400 transition"
        >
          <span className="text-xl">←</span> {t('mentorProfile.backToDashboard')}
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-6">{t('mentorProfile.heading')}</h2>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profilbild-Kachel */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow flex flex-col items-center">
          <div className="relative w-32 h-32">
            {displayedImage ? (
              <img
                src={displayedImage}
                alt={t('mentorProfile.avatarAlt')}
                className="w-32 h-32 rounded-full object-cover shadow-md"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center text-gray-400">
                {t('mentorProfile.noAvatar')}
              </div>
            )}
            <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-60 rounded-full cursor-pointer">
              <Camera className="w-8 h-8 text-white opacity-0 hover:opacity-100" />
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>
          <p className="text-sm text-gray-400 mt-2">{t('mentorProfile.changeAvatar')}</p>
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <label>{t('mentorProfile.firstName')}<ReqStar /></label>
          <input type="text" value={firstNameValue} onChange={(e) => setFirstNameValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.lastName')}<ReqStar /></label>
          <input type="text" value={lastNameValue} onChange={(e) => setLastNameValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.email')}</label>
          <input type="email" value={mailValue} readOnly className="w-full p-2 rounded bg-gray-700" />
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <label>{t('mentorProfile.phone')}</label>
          <input type="text" value={phoneValue} onChange={(e) => setPhoneValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.location')}</label>
          <input type="text" value={standortValue} onChange={(e) => setStandortValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('mentorProfile.department')}<ReqStar /></label>

          {/* ✅ geändert: Crash-sicher via tArray */}
          <select value={abteilungValue} onChange={(e) => setAbteilungValue(e.target.value)} className="w-full p-2 rounded bg-gray-700">
            {tArray('mentorProfile.departmentOptions').map((opt) => (
              <option key={opt}>{opt}</option>
            ))}
          </select>

          <label>{t('mentorProfile.jobTitle')}<ReqStar /></label>
          <input type="text" value={jobTitleValue} onChange={(e) => setJobTitleValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <label>
            {gtT(
              'mentorProfile.experienceSummaryLabel',
              'Beschreibe Deine Erfahrung kurz in 2-3 Sätzen',
              'Describe your experience briefly in 2–3 sentences'
            )}
            <ReqStar />
          </label>
          <textarea
            value={profileDescriptionValue}
            onChange={(e) => setProfileDescriptionValue(e.target.value)}
            className="w-full p-2 rounded bg-gray-700"
            rows={3}
            placeholder={gtT(
              'mentorProfile.experienceSummaryPlaceholder',
              'Z. B. Schwerpunkte, Domänen, typische Projekte…',
              'E.g., focus areas, domains, typical projects…'
            )}
          />

          <label>{t('mentorProfile.experience')}<ReqStar /></label>

          {/* ✅ geändert: Crash-sicher via tArray */}
          <select value={experienceValue} onChange={(e) => setExperienceValue(e.target.value)} className="w-full p-2 rounded bg-gray-700">
            {tArray('mentorProfile.experienceOptions').map((opt) => (
              <option key={opt}>{opt}</option>
            ))}
          </select>
        </div>

        <div className="md:grid md:grid-cols-2 md:gap-6 bg-gray-800 p-6 rounded-2xl shadow md:col-span-2">
          <div>
            <p className="text-lg font-semibold mb-2">
              {t('mentorProfile.goalsLabel')}
              <span className="text-xs text-gray-400">({t('mentorProfile.goalsMax')})</span>
              <ReqStar />
            </p>
            {devGoals.map((goal) => (
              <div key={goal} className="flex items-center mb-1">
                <input
                  type="checkbox"
                  checked={Array.isArray(goals) ? goals.includes(goal) : false}
                  onChange={() => handleDevGoalChange(goal)}
                  disabled={!(Array.isArray(goals) ? goals.includes(goal) : false) && (Array.isArray(goals) ? goals.length : 0) >= 4}
                  className="mr-2"
                />
                <label>{goal}</label>
              </div>
            ))}
            {(Array.isArray(goals) ? goals.length : 0) >= 4 && (
              <div className="text-xs text-mentixxgreen mt-1">{t('mentorProfile.goalsLimit')}</div>
            )}
          </div>

          <div>
            <p className="text-lg font-semibold mb-2">
              {t('mentorProfile.competencesLabel')}
              <span className="text-xs text-gray-400">({t('mentorProfile.competencesMax')})</span>
              <ReqStar />
            </p>
            {competenceData.map((cat) => (
              <div key={cat.kategorie} className="mb-6">
                <strong className="block mb-2">{cat.kategorie}</strong>
                <div className="grid grid-cols-2 gap-2 ml-4">
                  {cat.items.map((item) => (
                    <div key={item} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={Array.isArray(competences) ? competences.includes(item) : false}
                        onChange={() => handleCompetenceChange(item)}
                        disabled={!(Array.isArray(competences) ? competences.includes(item) : false) && (Array.isArray(competences) ? competences.length : 0) >= 16}
                        className="mr-2"
                      />
                      <label>{item}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {(Array.isArray(competences) ? competences.length : 0) >= 16 && (
              <div className="text-xs text-mentixxgreen mt-1">{t('mentorProfile.competencesLimit')}</div>
            )}
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow md:col-span-2">
          <label>{t('mentorProfile.whyMentoring')}</label>
          <textarea value={whyMentoring} onChange={(e) => setWhyMentoring(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
        </div>

        {/* GreenTech Matching Prefs (Survey 28) */}
        {gtPrefsAvailable && (
          <div className="md:col-span-2 border border-gray-700 rounded-xl p-4 bg-gray-900/40">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">
                  {gtT(
                    'mentorProfile.gtPrefs.title',
                    'Matching-Präferenzen (GreenTech Talents)',
                    'Matching preferences (GreenTech Talents)'
                  )}
                </h3>
                <p className="text-sm text-gray-300 mt-1">
                  {gtT(
                    'mentorProfile.gtPrefs.subtitle',
                    'Diese Angaben gelten programm-spezifisch (Survey 28) und werden beim Matching berücksichtigt. Sprache ist ein hartes Muss.',
                    'These preferences are program-specific (Survey 28) and are used for matching. Language is a hard requirement.'
                  )}
                </p>
              </div>
              {gtPrefsLoading && (
                <div className="text-sm text-gray-400">
                  {gtT('mentorProfile.gtPrefs.loading', 'Lade…', 'Loading…')}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Sprache */}
              <div>
                <p className="text-sm font-semibold">
                  {gtT('mentorProfile.gtPrefs.languageLabel', 'Sprache (hartes Muss)', 'Language (hard requirement)')}
                  <ReqStar />
                </p>
                <div className="flex flex-wrap gap-3 mt-2">
                  {GREENTECH_LANGUAGE_OPTIONS.map(opt => (
                    <label key={opt.value} className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={gtLanguages.includes(opt.value)}
                        onChange={() => toggleInList(opt.value, gtLanguages, setGtLanguages)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Mentor Background */}
              <div>
                <p className="text-sm font-semibold">
                  {gtT('mentorProfile.gtPrefs.backgroundLabel', 'Du bist … (hartes Muss)', 'You are … (hard requirement)')}
                  <ReqStar />
                </p>
                <div className="flex flex-col gap-2 mt-2 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="gtMentorBackground"
                      value="science"
                      checked={gtMentorBackground === 'science'}
                      onChange={() => {
                        setGtPrefsSavedOk(false);
                        setGtPrefsMessage('');
                        setGtPrefsError('');
                        setGtMentorBackground('science');
                      }}
                    />
                    <span>
                      {gtT(
                        'mentorProfile.gtPrefs.backgroundScience',
                        'Wissenschaft / Forschung (Fraunhofer, Helmholtz, DFKI, Uni …)',
                        'Academia / research (Fraunhofer, Helmholtz, DFKI, university …)'
                      )}
                    </span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="radio"
                      name="gtMentorBackground"
                      value="industry"
                      checked={gtMentorBackground === 'industry'}
                      onChange={() => {
                        setGtPrefsSavedOk(false);
                        setGtPrefsMessage('');
                        setGtPrefsError('');
                        setGtMentorBackground('industry');
                      }}
                    />
                    <span>
                      {gtT(
                        'mentorProfile.gtPrefs.backgroundIndustry',
                        'Industrie (z. B. Siemens R&D …)',
                        'Industry (e.g., Siemens R&D …)'
                      )}
                    </span>
                  </label>
                </div>

                <div className="mt-4">
                  <p className="text-sm font-semibold">
                    {gtT(
                      'mentorProfile.gtPrefs.maxMenteesLabel',
                      'Max. Anzahl Mentees in diesem Programm',
                      'Max. number of mentees in this program'
                    )}
                    <ReqStar />
                  </p>
                  <select
                    value={gtMentorMaxMentees ?? ''}
                    onChange={(e) => {
                      setGtPrefsSavedOk(false);
                      setGtPrefsMessage('');
                      setGtPrefsError('');
                      setGtMentorMaxMentees(e.target.value ? Number(e.target.value) : null);
                    }}
                    className="w-full p-2 rounded bg-gray-700 mt-2"
                  >
                    <option value="" disabled>
                      {gtT('mentorProfile.gtPrefs.selectPlaceholder', 'Bitte wählen…', 'Please select…')}
                    </option>
                    {[1, 2, 3, 4, 5].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Application fields */}
              <div>
                <p className="text-sm font-semibold">
                  {gtT(
                    'mentorProfile.gtPrefs.applicationFieldsLabel',
                    `Anwendungsfelder (max. ${GREENTECH_MAX_SELECTION})`,
                    `Application fields (max. ${GREENTECH_MAX_SELECTION})`
                  )}
                  <ReqStar />
                </p>

                <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2 text-sm">
                  {GREENTECH_APPLICATION_FIELDS.map(field => (
                    <label key={field} className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={gtApplicationFields.includes(field)}
                        onChange={() => toggleInList(field, gtApplicationFields, setGtApplicationFields, GREENTECH_MAX_SELECTION)}
                        disabled={!gtApplicationFields.includes(field) && gtApplicationFields.length >= GREENTECH_MAX_SELECTION}
                      />
                      <span>{field}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Methods & skills */}
              <div>
                <p className="text-sm font-semibold">
                  {gtT(
                    'mentorProfile.gtPrefs.methodsSkillsLabel',
                    `Methoden / Skills (max. ${GREENTECH_MAX_SELECTION})`,
                    `Methods / skills (max. ${GREENTECH_MAX_SELECTION})`
                  )}
                  <ReqStar />
                </p>

                <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2 text-sm">
                  {GREENTECH_METHODS_SKILLS.map(skill => (
                    <label key={skill} className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={gtMethodsSkills.includes(skill)}
                        onChange={() => toggleInList(skill, gtMethodsSkills, setGtMethodsSkills, GREENTECH_MAX_SELECTION)}
                        disabled={!gtMethodsSkills.includes(skill) && gtMethodsSkills.length >= GREENTECH_MAX_SELECTION}
                      />
                      <span>{skill}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {(gtPrefsError || gtPrefsMessage) && (
              <div className="mt-4 text-sm">
                {gtPrefsError && <div className="text-red-300">{gtPrefsError}</div>}
                {!gtPrefsError && gtPrefsMessage && <div className="text-green-300">{gtPrefsMessage}</div>}
              </div>
            )}

            <div className="mt-4 flex items-start gap-3">
              <div>
                <button
                  type="button"
                  onClick={() => saveGreenTechPrefs()}
                  disabled={gtPrefsSaving || gtPrefsLoading || !gtPrefsIsValid}
                  className={`bg-mentixxgreen text-black font-bold py-2 px-4 rounded ${
                    (gtPrefsSaving || gtPrefsLoading || !gtPrefsIsValid) ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  {gtPrefsSaving
                    ? gtT('mentorProfile.gtPrefs.saving', 'Speichere…', 'Saving…')
                    : gtT('mentorProfile.gtPrefs.saveButton', 'Prefs speichern', 'Save preferences')}
                </button>

                {!gtPrefsIsValid && (
                  <div className="text-xs text-yellow-200 mt-2">
                    {gtPrefsMissingHint}
                  </div>
                )}
              </div>

              <span className="text-xs text-gray-400 mt-2">
                {gtT(
                  'mentorProfile.gtPrefs.hint',
                  'Wird pro Programm gespeichert.',
                  'Saved per program.'
                )}
              </span>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={!profileSaveIsAllowed}
          className={`bg-mentixxgreen w-full py-2 rounded text-black font-bold md:col-span-2 ${
            !profileSaveIsAllowed ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          {tr('Profil speichern / MentorMatch-Analyse', 'Save profile / MentorMatch analysis')}
        </button>

        {showSaveMissingFieldsHint && (
          <div className="md:col-span-2 text-xs text-yellow-200">
            {tr('Bitte noch ausfüllen:', 'Please fill:')}{' '}
            {missingProfileFields.join(', ')}
          </div>
        )}

        {!showSaveMissingFieldsHint && showPrefsMustBeSavedHint && (
          <div className="md:col-span-2 text-xs text-yellow-200">
            {gtT(
              'mentorProfile.gtPrefs.mustBeSaved',
              'Bitte zuerst die Matching-Präferenzen speichern.',
              'Please save the matching preferences first.'
            )}
          </div>
        )}
      </form>

      {/* Big Five Analyse */}
      {bigfiveData && bigfiveData.extraversion && (
        <div className="mt-10 flex justify-center">
          <div className="bg-[#23272a] rounded-3xl shadow-lg p-8 max-w-xl w-full mx-auto">
            <div className="text-2xl font-bold text-center mb-6 text-white tracking-tight">{t('mentorProfile.bigFiveTitle')}</div>
            <div className="space-y-7 mb-5">
              {/* ✅ geändert: Crash-sicher via tArray */}
              {tArray('mentorProfile.bigFiveTraits').map((trait) => {
                const raw = parseFloat(bigfiveData[trait.key]) || 0;
                const percent = Math.round((raw / 5) * 100);
                return (
                  <div key={trait.key}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold" style={{ color: trait.color }}>{trait.label}</span>
                      <span className="text-xs text-gray-300">{percent} / 100</span>
                    </div>
                    <div className="relative flex items-center h-6 mb-1">
                      <span className="absolute left-0 text-xs text-gray-400 select-none" title={trait.min}>0</span>
                      <span className="absolute right-0 text-xs text-gray-400 select-none" title={trait.max}>100</span>
                      <div className="absolute left-0 w-full h-3 bg-gray-900 rounded-full" />
                      <div
                        className="absolute left-0 h-3 rounded-full"
                        style={{
                          width: `${percent}%`,
                          background: `linear-gradient(90deg, ${trait.color} 70%, #23272a 100%)`
                        }}
                        title={percent < 34 ? trait.min : percent < 67 ? trait.mid : trait.max}
                      />
                      <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-6 bg-gray-600 rounded opacity-60" title={trait.mid}></div>
                    </div>
                    <div className="text-xs text-gray-400 text-center mt-0.5">{`50 / 100: ${trait.mid}`}</div>
                  </div>
                );
              })}
            </div>
            <div className="bg-[#1a1b1f] rounded-xl p-5 shadow-inner mt-7 space-y-3 text-[15px] leading-relaxed">
              <div><b>{t('mentorProfile.traitExtraversion')}:</b> {bigfiveData.extraversion_text || '—'}</div>
              <div><b>{t('mentorProfile.traitConscientiousness')}:</b> {bigfiveData.gewissenhaftigkeit_text || '—'}</div>
              <div><b>{t('mentorProfile.traitEmotionalStability')}:</b> {bigfiveData.negative_emotionalitaet_text || '—'}</div>
              <div><b>{t('mentorProfile.traitAgreeableness')}:</b> {bigfiveData.vertraeglichkeit_text || '—'}</div>
              <div><b>{t('mentorProfile.traitOpenness')}:</b> {bigfiveData.offenheit_text || '—'}</div>
            </div>
          </div>
        </div>
      )}

      <BigFiveModal
        visible={showBigFiveModal}
        onClose={() => setShowBigFiveModal(false)}
        onStartTest={async () => {
          setShowBigFiveModal(false);
          const newWindow = window.open('about:blank', '_blank');

          const urlLang =
            new URLSearchParams(window.location.search).get('lang') ||
            (typeof i18n?.language === 'string' ? i18n.language : 'de');
          const lang = String(urlLang).slice(0, 2);

          const who = await fetch('/whoami', { credentials: 'include' })
            .then(r => r.json())
            .catch(() => null);

          const authUserId = who?.user?.id;
          if (!authUserId) {
            if (newWindow) newWindow.close();
            alert('Login fehlt oder Session abgelaufen. Bitte neu einloggen.');
            return;
          }

          // BigFive läuft unter /bigfive/app/ (wichtig: trailing slash!)
          const url = `/bigfive/app/?user_id=${authUserId}&lng=${lang}`;
          if (newWindow) {
            newWindow.location.href = url;
          } else {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }}
      />
    </div>
  );
}

export default MentorProfilePage;
